import type { KanjiResult, RadicalInfo } from "@/lib/search";

/**
 * The radical picker's pure parts, kept out of the component so they can be unit-tested.
 *
 * The picker is an **input method, not a search mode** (README `4a`): it is reached from a caret,
 * and it ends by putting a character at that caret. `insertAtCaret` is therefore the load-bearing
 * function here, not the palette maths.
 */

/** ~9, per the design — the row exists because you reach for the same handful constantly. */
export const MAX_RECENTS = 9;

export interface StrokeGroup<T> {
  strokeCount: number;
  items: T[];
}

/**
 * Group by stroke count, ascending. The catalog already arrives sorted by (strokeCount, radical)
 * from the server, but grouping here rather than trusting that keeps the component honest if the
 * caller ever hands it a filtered list (the recents row does exactly that).
 */
export function groupByStroke<T>(items: readonly T[], strokeOf: (item: T) => number): StrokeGroup<T>[] {
  const groups = new Map<number, T[]>();
  for (const item of items) {
    const stroke = strokeOf(item);
    const bucket = groups.get(stroke);
    if (bucket) bucket.push(item);
    else groups.set(stroke, [item]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a - b)
    .map(([strokeCount, items]) => ({ strokeCount, items }));
}

export function groupRadicals(catalog: readonly RadicalInfo[]): StrokeGroup<RadicalInfo>[] {
  return groupByStroke(catalog, (r) => r.strokeCount);
}

export function groupKanji(kanji: readonly KanjiResult[]): StrokeGroup<KanjiResult>[] {
  return groupByStroke(kanji, (k) => k.strokeCount);
}

export type RadicalState = "selected" | "available" | "unavailable";

/**
 * Cell state. With nothing selected every radical is available — the server returns no
 * `availableRadicals` for an empty selection, and greying out the entire palette on open would be
 * the wrong reading of that.
 */
export function radicalState(
  radical: string,
  selected: readonly string[],
  available: ReadonlySet<string> | null,
): RadicalState {
  if (selected.includes(radical)) return "selected";
  if (available === null) return "available";
  return available.has(radical) ? "available" : "unavailable";
}

/** Toggle, preserving the palette's own order so selections do not jump around as you add them. */
export function toggleRadical(selected: readonly string[], radical: string): string[] {
  return selected.includes(radical)
    ? selected.filter((r) => r !== radical)
    : [...selected, radical];
}

/** Most-recently-used first, no duplicates, capped. */
export function rememberRadical(recents: readonly string[], radical: string): string[] {
  return [radical, ...recents.filter((r) => r !== radical)].slice(0, MAX_RECENTS);
}

export interface CaretInsertion {
  text: string;
  /** Where the caret lands — after the inserted text, ready for the next character. */
  caret: number;
}

/**
 * Insert at the caret. The caret is clamped rather than trusted: it comes from a DOM selection that
 * may be stale by the time the picker returns (the sentence can be re-parsed while it is open), and
 * an out-of-range slice would silently drop text.
 */
export function insertAtCaret(text: string, caret: number, insert: string): CaretInsertion {
  const at = Math.max(0, Math.min(caret, text.length));
  return { text: text.slice(0, at) + insert + text.slice(at), caret: at + insert.length };
}
